import React, { useEffect, useState } from 'react';
import { format } from '@querodelivery/qd-date-helper';
import { useNavigate } from 'react-router';

import { Button, Switch, Text } from '@querodelivery/uni-react';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip from 'rc-tooltip';
import styles from './FranchiseCard.module.scss';

import { ROLE_TYPES, type Franchise } from '@/Interfaces/Data';
import { moneyLabel } from '@/Utils/FnUtils';
import { useAppDispatch, useAppSelector } from '@/_store/hooks';
import { showFranchiseModal } from '@/_store/modules/franchise';
import { useUpdateIsCloud } from '@/features/fare/api/put-is-cloud';
import { useUpdateHasCloudOperation } from '@/features/fare/api/put-has-cloud-operation';
import { selectUser } from '@/_store/modules/auth/selectors';
import { GET_FARES } from '@/api/fares/get-fares';
import {
  ToggleCloudConfirmModal,
  type ToggleCloudTarget,
} from './toggle-cloud-confirm-modal/toggle-cloud-confirm-modal';
import { queryClient } from '@/_config/lib/react-query/query-client';

interface FranchiseCardProps {
  franchise: Franchise;
}

function FranchiseCard({ franchise }: FranchiseCardProps) {
  const dispatch = useAppDispatch();

  const user = useAppSelector(selectUser);

  const navigate = useNavigate();

  const [isCloud, setIsCloud] = useState(!!franchise.isCloud);
  const [hasCloudOperationOnCity, setHasCloudOperationOnCity] = useState(
    !!franchise.hasCloudOperationOnCity,
  );
  const [confirmTarget, setConfirmTarget] = useState<ToggleCloudTarget | null>(
    null,
  );

  const { mutate: putUpdateIsCloud, isLoading: isUpdatingIsCloud } =
    useUpdateIsCloud();
  const {
    mutate: putUpdateHasCloudOperation,
    isLoading: isUpdatingHasCloudOperation,
  } = useUpdateHasCloudOperation();

  useEffect(() => {
    setIsCloud(!!franchise.isCloud);
  }, [franchise.isCloud]);

  useEffect(() => {
    setHasCloudOperationOnCity(!!franchise.hasCloudOperationOnCity);
  }, [franchise.hasCloudOperationOnCity]);

  const creationDate = format(new Date(franchise.createdAt), 'dd/MM/yyyy');

  function openFranchiseModal() {
    dispatch(showFranchiseModal(franchise));
  }

  function handleCloseConfirmModal() {
    setConfirmTarget(null);
  }

  const isAdmin = user?.managerLogType === ROLE_TYPES.ADMIN;
  const isLogisticOperator =
    user?.managerLogType === ROLE_TYPES.LOGISTIC_OPERATOR;

  const canEditCityFare =
    !isLogisticOperator ||
    !!user?.permissions?.city?.editFare ||
    !!user?.permissions?.city?.all;

  const canManageCloudToggles = !isLogisticOperator;
  const canViewPlaceFares = !isLogisticOperator;

  const canToggleCloudForDeliveryman =
    canManageCloudToggles &&
    (isAdmin ||
      !!user?.permissions?.city?.all ||
      !!user?.permissions?.city?.editFare);

  const canToggleCloudForPartners =
    canManageCloudToggles && (isAdmin || !!user?.permissions?.city?.all);

  const handleUpdateFranchise: React.MouseEventHandler<
    HTMLButtonElement
  > = event => {
    event.stopPropagation();

    navigate(`/tarifas/municipio/editar/${franchise._id}`);
  };

  const handleNavigateToPlaceFares: React.MouseEventHandler<
    HTMLButtonElement
  > = event => {
    event.stopPropagation();

    navigate(`${franchise._id}`);
  };

  const handleToggleCloud =
    (target: ToggleCloudTarget): React.MouseEventHandler<HTMLButtonElement> =>
    event => {
      event.stopPropagation();
      setConfirmTarget(target);
    };

  function handleConfirmToggleCloud() {
    if (confirmTarget === 'deliverymen') {
      putUpdateIsCloud(
        { cityId: franchise._id, isCloud: !isCloud },
        {
          onSuccess: () => {
            setIsCloud(prev => !prev);
            handleCloseConfirmModal();
            queryClient.invalidateQueries({ queryKey: [GET_FARES] });
          },
        },
      );
      return;
    }

    if (confirmTarget === 'partners') {
      putUpdateHasCloudOperation(
        {
          cityId: franchise._id,
          hasCloudOperationOnCity: !hasCloudOperationOnCity,
        },
        {
          onSuccess: () => {
            setHasCloudOperationOnCity(prev => !prev);
            handleCloseConfirmModal();
            queryClient.invalidateQueries({ queryKey: [GET_FARES] });
          },
        },
      );
    }
  }

  const isConfirmModalOpen = confirmTarget !== null;
  const isActivating =
    confirmTarget === 'partners' ? !hasCloudOperationOnCity : !isCloud;
  const isUpdating =
    confirmTarget === 'partners'
      ? isUpdatingHasCloudOperation
      : isUpdatingIsCloud;

  return (
    <>
      <div className={styles.franchiseCard} onClick={openFranchiseModal}>
        <div className={styles.franchiseInfo}>
          <span>
            <Text className={styles.franchiseInfoText} variant="headingMd">
              Cidade:
            </Text>{' '}
            <Text variant="bodyMd">{`${franchise.nome} - ${franchise.uf}`}</Text>{' '}
          </span>

          <span>
            <Text className={styles.franchiseInfoText} variant="headingMd">
              Valor entrega base:{' '}
            </Text>{' '}
            <Text variant="bodyMd">
              {moneyLabel(franchise.valorEntregaBase)}
            </Text>
          </span>

          <span>
            <Text className={styles.franchiseInfoText} variant="headingMd">
              Data de criação:{' '}
            </Text>{' '}
            <Text variant="bodyMd">{creationDate}</Text>
          </span>

          <span>
            <Text className={styles.franchiseInfoText} variant="headingMd">
              Taxa entrega base:
            </Text>{' '}
            <Text variant="bodyMd">
              {moneyLabel(franchise.taxaEntregaBase)}
            </Text>
          </span>
        </div>

        <div
          className={`${styles.franchiseActions}${
            !canManageCloudToggles
              ? ` ${styles.franchiseActionsOnlyButtons}`
              : ''
          }`}
        >
          {canManageCloudToggles && (
            <div className={styles.switches}>
              <Text className={styles.franchiseInfoText} variant="headingMd">
                Cadastro nuvem ativo{' '}
                <Tooltip
                  overlay={
                    <div>
                      Parceiros: controla o banner de nuvem que será exibido
                      <br />
                      Entregadores: permite o cadastro de entregadores na nuvem
                    </div>
                  }
                  placement="top"
                  styles={{ body: { maxWidth: '260px' } }}
                >
                  <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
                </Tooltip>
              </Text>
              <Switch
                checked={isCloud}
                label="Para entregadores"
                labelPosition="right"
                variant="success-warning"
                onClick={handleToggleCloud('deliverymen')}
                disabled={!canToggleCloudForDeliveryman}
              />
              <Switch
                checked={hasCloudOperationOnCity}
                label="Para parceiros"
                labelPosition="right"
                variant="success-warning"
                onClick={handleToggleCloud('partners')}
                disabled={!canToggleCloudForPartners}
              />
            </div>
          )}

          <div className={styles.buttons}>
            {canViewPlaceFares && (
              <Button
                variant="outline"
                className={styles.button}
                onClick={handleNavigateToPlaceFares}
              >
                <Text variant="bodySm" tone="supporting" fontWeight="medium">
                  Visualizar tarifas da cidade
                </Text>
              </Button>
            )}
            <Button
              variant="outline"
              className={styles.button}
              onClick={handleUpdateFranchise}
            >
              <Text variant="bodySm" tone="supporting" fontWeight="medium">
                {canEditCityFare ? 'Atualizar tarifa' : 'Visualizar tarifa'}
              </Text>
            </Button>
          </div>
        </div>
      </div>

      {confirmTarget && (
        <ToggleCloudConfirmModal
          open={isConfirmModalOpen}
          cityName={franchise.nome}
          isActivating={isActivating}
          target={confirmTarget}
          loading={isUpdating}
          onClose={handleCloseConfirmModal}
          onCancel={handleCloseConfirmModal}
          onConfirm={handleConfirmToggleCloud}
        />
      )}
    </>
  );
}

export default FranchiseCard;
